import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BrandService } from 'src/app/services/brand.service';
import { CategoryService } from 'src/app/services/category.service';
import { ProductService } from 'src/app/services/product.service';
import { SapCategoryService } from 'src/app/services/sap-category.service';

@Component({
  standalone: false,
  selector: 'app-products-add',
  templateUrl: './products-add.component.html',
})
export class ProductsAddComponent implements OnInit {
  brands: any[] = [];
  categories: any[] = [];
  sapCategories: any[] = [];
  form: any = {
    brand_id: '',
    group_id: '',
    stock_id: '',
    barcode: '',
    sku: '',
    name: '',
    base_price: '',
    description: '',
    special_remarks: '',
    status: 1,
    category_key: '',
  };
  validationTriggered = false;

  constructor(
    private toaster: ToastrService,
    private router: Router,
    private productService: ProductService,
    private brandService: BrandService,
    private categoryService: CategoryService,
    private sapCategoryService: SapCategoryService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.brandService.list().subscribe({
      next: (result) => {
        this.brands = result?.data?.brands ?? [];
        this.cdr.detectChanges();
      },
      error: () => this.toaster.error('Failed to load brands.'),
    });
    this.categoryService.list().subscribe({
      next: (result) => {
        this.categories = result?.data?.categories ?? [];
        this.cdr.detectChanges();
      },
      error: () => this.toaster.error('Failed to load categories.'),
    });
    this.sapCategoryService.list().subscribe({
      next: (result) => {
        this.sapCategories = result?.data?.sap_categories ?? [];
        this.cdr.detectChanges();
      },
      error: () => this.toaster.error('Failed to load SAP categories.'),
    });
  }

  submit(): void {
    this.validationTriggered = true;
    const required = ['brand_id', 'stock_id', 'barcode', 'sku', 'name', 'base_price'];
    if (required.some((key) => this.form[key] === '' || this.form[key] === null || this.form[key] === undefined)) {
      this.toaster.error('Please fill in all required fields.');
      return;
    }

    const categoryIds = this.resolveCategoryIds(this.form.category_key);
    if (categoryIds === null) {
      return;
    }

    const payload = {
      brand_id: Number(this.form.brand_id),
      group_id: this.form.group_id === '' ? null : Number(this.form.group_id),
      stock_id: this.form.stock_id,
      barcode: this.form.barcode,
      sku: this.form.sku,
      name: this.form.name,
      base_price: Number(this.form.base_price),
      description: this.form.description || null,
      special_remarks: this.form.special_remarks || null,
      status: Number(this.form.status),
      category_ids: categoryIds,
    };

    this.productService.create(payload).subscribe({
      next: (result) => {
        this.toaster.success('Product added.');
        this.router.navigate(['/ecomm/products', result.data.id]);
      },
      error: (error) => {
        this.toaster.error(error?.error?.message || 'Failed to add product.');
      },
    });
  }

  /**
   * Products persist category_ids against `categories` only.
   * SAP category picks resolve to a Category with the same name when possible.
   */
  private resolveCategoryIds(categoryKey: string): number[] | null {
    if (!categoryKey) {
      return [];
    }

    if (categoryKey.startsWith('c:')) {
      const id = Number(categoryKey.slice(2));
      return Number.isFinite(id) ? [id] : [];
    }

    if (categoryKey.startsWith('s:')) {
      const sapId = Number(categoryKey.slice(2));
      const sap = this.sapCategories.find((c) => c.id === sapId);
      if (!sap) {
        this.toaster.error('Selected SAP category was not found.');
        return null;
      }
      const match = this.categories.find(
        (c) => String(c.name).trim().toLowerCase() === String(sap.name).trim().toLowerCase(),
      );
      if (!match) {
        this.toaster.error(
          `No Category named "${sap.name}". Create it under Categories, or pick a Category.`,
        );
        return null;
      }
      return [match.id];
    }

    return [];
  }
}
